import { StateCreator } from "zustand";
import { State } from "./types";
import { StripeSubscriptionData } from "@/lib/types/stripe.types";
import { supabase } from "../../../../lib/supabase";
import { SupabaseWriteTableEnum } from "../../../../lib/supabase/supabaseTypes";
import { Logger } from "../../../../lib/logger/Logger";

export interface SubscriptionSlice {
	getSubscription: () => Promise<void>;
	getTrialDaysRemaining: () => number;
}

export const createSubscriptionSlice: StateCreator<
	State & SubscriptionSlice,
	[],
	[],
	SubscriptionSlice
> = (set, get) => ({
	getSubscription: async () => {
		const organization = get().organization;
		if (!organization?.id) {
			Logger.error("Organization is not set");
			return;
		}

		let subscription = organization.stripe_data as StripeSubscriptionData | null;

		if (
			subscription &&
			subscription.status === "trialing" &&
			!subscription.subscription_id &&
			organization.trial_period_end &&
			new Date(organization.trial_period_end) < new Date()
		) {
			subscription.status = "canceled";
			subscription.trial_period_end = new Date(
				organization.trial_period_end
			).toISOString();
			const { error: updateError } = await supabase
				.from(SupabaseWriteTableEnum.ORGANIZATIONS)
				.update({
					stripe_data: subscription,
				})
				.eq("id", organization.id);
			if (updateError) {
				Logger.error(updateError, {}, "Error updating subscription");
			}
		}

		if (subscription) {
			subscription = {
				...subscription,
				trial_period_end: organization.trial_period_end
					? new Date(organization.trial_period_end).toISOString()
					: null,
			};
		} else {
			const isTrial =
				organization.trial_period_end &&
				new Date(organization.trial_period_end).getTime() >
				new Date().getTime();
			subscription = {
				status: isTrial ? "trialing" : "",
				subscription_id: null,
				customer_id: null,
				trial_period_end: organization.trial_period_end,
			} as StripeSubscriptionData;
		}

		set({
			subscription: subscription as StripeSubscriptionData | null,
		});
	},

	getTrialDaysRemaining: () => {
		const { subscription } = get();
		if (!subscription?.trial_period_end) return 0;

		const trialEnd = new Date(subscription.trial_period_end).getTime();
		const now = new Date().getTime();
		const daysRemaining = Math.ceil(Math.max(trialEnd - now, 0) / (1000 * 60 * 60 * 24));

		return daysRemaining;
	},
});
